import { useState, useEffect, useCallback } from 'react'
import { generateReport } from '@/api/reports'
import type { ReportType } from '@isapi/shared'

const TABS: { id: ReportType; label: string }[] = [
  { id: 'attendance_summary', label: 'Asistencia' },
  { id: 'revenue', label: 'Revenue' },
  { id: 'expiring_memberships', label: 'Vencimientos' },
]

const COLUMN_LABELS: Record<string, Record<string, string>> = {
  attendance_summary: { employee_no: 'Empleado', occurred_at: 'Fecha/hora', event_type: 'Tipo' },
  revenue: { amount: 'Monto', created_at: 'Fecha', plan_name: 'Plan' },
  expiring_memberships: { user_name: 'Miembro', end_date: 'Vence', plan_name: 'Plan' },
}

export function ReportDashboard() {
  const [activeTab, setActiveTab] = useState<ReportType>('attendance_summary')
  const [rows, setRows] = useState<Record<string, unknown>[]>([])
  const [columns, setColumns] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await generateReport(activeTab, undefined)
      setRows(result.rows)
      setColumns(Object.keys(result.rows[0] ?? {}))
    } catch {
      setError('Error al cargar el reporte. Intenta de nuevo.')
      setRows([])
      setColumns([])
    } finally {
      setLoading(false)
    }
  }, [activeTab])

  useEffect(() => { load() }, [load])

  const labelFor = (col: string) => COLUMN_LABELS[activeTab]?.[col] ?? col

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-text-primary font-semibold text-lg">Reportes</h1>

      <div role="tablist" className="flex gap-1 bg-surface border border-border rounded-lg p-1 w-fit">
        {TABS.map(tab => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`panel-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-accent text-background'
                : 'text-text-muted hover:text-text-primary'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div role="tabpanel" id={`panel-${activeTab}`}>
        {loading && <p className="text-text-muted text-sm">Cargando...</p>}
        {error && <p className="text-destructive text-sm">{error}</p>}
        {!loading && !error && (
          <div className="bg-surface rounded-lg border border-border overflow-auto">
            {rows.length === 0 ? (
              <p className="px-4 py-6 text-center text-text-muted text-xs">Sin datos para este reporte.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    {columns.map(col => (
                      <th key={col} className="text-left px-4 py-2.5 text-text-muted text-xs">
                        {labelFor(col)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      {columns.map(col => (
                        <td key={col} className="px-4 py-3 text-text-muted text-xs">
                          {String(row[col] ?? '—')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
